import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, X, MapPin } from "lucide-react";

export default function Connections() {
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("accepted");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connections");
      setConns(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    try {
      await api.post(`/connections/${id}/accept`);
      toast.success("Connection accepted");
      load();
    } catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/connections/${id}/reject`);
      toast.success("Declined");
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/connections/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = conns.filter((c) => {
    if (tab === "accepted") return c.status === "accepted";
    if (tab === "incoming") return c.status === "pending" && c.direction === "incoming";
    if (tab === "outgoing") return c.status === "pending" && c.direction === "outgoing";
    return false;
  });

  const tabs = [
    { id: "accepted", label: "Connected", count: conns.filter((c) => c.status === "accepted").length },
    { id: "incoming", label: "Invitations", count: conns.filter((c) => c.status === "pending" && c.direction === "incoming").length },
    { id: "outgoing", label: "Sent", count: conns.filter((c) => c.status === "pending" && c.direction === "outgoing").length },
  ];

  return (
    <div className="min-h-screen bg-[#0A0C10] pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="overline">Your relationships</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-2 tracking-tight">Connections</h1>

        <div className="flex gap-2 mt-10 border-b border-white/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm transition-colors relative ${
                tab === t.id ? "text-[#7DA3D9]" : "text-slate-400 hover:text-white"
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}{" "}
              <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-white/5 border border-white/10">
                {t.count}
              </span>
              {tab === t.id && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#7DA3D9]" />}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="overline text-center py-16">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="connections-empty">
            Nothing here yet.
          </div>
        ) : (
          <div className="space-y-3 mt-8" data-testid="connections-list">
            {filtered.map((c) => (
              <div key={c.id} className="glass-card p-5 flex items-center gap-4" data-testid={`connection-${c.id}`}>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7DA3D9]/40 to-[#1E293B] border border-white/10 flex items-center justify-center font-display text-[#B8D0EE]">
                  {c.other_user?.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{c.other_user?.name}</div>
                  <div className="text-xs text-slate-400 inline-flex items-center gap-2">
                    <span className="capitalize">{c.other_user?.role}</span>
                    {c.other_user?.country && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.other_user.country}</span>
                      </>
                    )}
                  </div>
                  {c.message && tab === "incoming" && (
                    <div className="text-sm text-slate-300 mt-2 italic">"{c.message}"</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {tab === "incoming" && (
                    <>
                      <button onClick={() => accept(c.id)} className="btn-gold text-sm px-4 py-2 inline-flex items-center gap-1" data-testid={`accept-${c.id}`}>
                        <Check className="w-4 h-4" /> Accept
                      </button>
                      <button onClick={() => reject(c.id)} className="btn-ghost-gold text-sm px-4 py-2 inline-flex items-center gap-1" data-testid={`reject-${c.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {tab === "outgoing" && (
                    <button onClick={() => remove(c.id)} className="btn-ghost-gold text-sm px-4 py-2" data-testid={`cancel-${c.id}`}>
                      Cancel
                    </button>
                  )}
                  {tab === "accepted" && (
                    <button onClick={() => remove(c.id)} className="text-slate-500 hover:text-red-400 p-2 transition-colors" data-testid={`remove-${c.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
